import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function DocumentTypesManager({ usuario }) {
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        tipo_documento: '',
        descripcion: '',
        prompt: '',
        tipo_vencimiento: 'fijo',
        dia_vencimiento: '',
        tipo_entidad: 'P',
        activo: true,
        base: false
    });

    const [vencimientos, setVencimientos] = useState([]);
    const [selectedExamplesType, setSelectedExamplesType] = useState(null);
    const [examplesByType, setExamplesByType] = useState({});
    const [examplesLoadingId, setExamplesLoadingId] = useState(null);
    const [examplesError, setExamplesError] = useState('');
    const [deletingExampleId, setDeletingExampleId] = useState('');
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroEntidad, setFiltroEntidad] = useState('');
    const [filtroActivo, setFiltroActivo] = useState('');

    useEffect(() => {
        fetchTipos();
        fetchVencimientos();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const normalizeExampleEntries = (value) => {
        if (!value) return [];

        const normalizeItem = (item, index = 0) => {
            const isStringValue = typeof item === 'string';
            const parsedItem = !isStringValue && item && typeof item === 'object' ? item : {};
            const texto = typeof parsedItem.texto === 'string'
                ? parsedItem.texto.trim()
                : typeof parsedItem.contenido === 'string'
                    ? parsedItem.contenido.trim()
                    : typeof parsedItem.extracted_text === 'string'
                        ? parsedItem.extracted_text.trim()
                        : isStringValue
                            ? item.trim()
                            : '';

            return {
                example_id: typeof parsedItem.example_id === 'string' && parsedItem.example_id.trim()
                    ? parsedItem.example_id.trim()
                    : `legacy-${index}`,
                texto,
                nombre_archivo: typeof parsedItem.nombre_archivo === 'string' && parsedItem.nombre_archivo.trim()
                    ? parsedItem.nombre_archivo.trim()
                    : typeof parsedItem.file_name === 'string' && parsedItem.file_name.trim()
                        ? parsedItem.file_name.trim()
                        : null,
                ruta_archivo: typeof parsedItem.ruta_archivo === 'string' && parsedItem.ruta_archivo.trim()
                    ? parsedItem.ruta_archivo.trim()
                    : null,
                mimetype: typeof parsedItem.mimetype === 'string' && parsedItem.mimetype.trim()
                    ? parsedItem.mimetype.trim()
                    : null,
                fecha_creacion: typeof parsedItem.fecha_creacion === 'string' && parsedItem.fecha_creacion.trim()
                    ? parsedItem.fecha_creacion.trim()
                    : typeof parsedItem.created_at === 'string' && parsedItem.created_at.trim()
                        ? parsedItem.created_at.trim()
                        : null,
                legacy: isStringValue || !parsedItem.ruta_archivo
            };
        };

        if (Array.isArray(value)) {
            return value
                .map((item, index) => normalizeItem(item, index))
                .filter(item => item.texto || item.ruta_archivo || item.nombre_archivo);
        }

        if (typeof value !== 'string') return [];

        const trimmed = value.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((item, index) => normalizeItem(item, index))
                    .filter(item => item.texto || item.ruta_archivo || item.nombre_archivo);
            }
            if (typeof parsed === 'object' && parsed !== null) {
                return [normalizeItem(parsed, 0)].filter(item => item.texto || item.ruta_archivo || item.nombre_archivo);
            }
            if (typeof parsed === 'string') {
                const single = parsed.trim();
                return single ? [normalizeItem(single, 0)] : [];
            }
        } catch (error) {
            // Mantener compatibilidad con el formato anterior de texto plano.
        }

        return [normalizeItem(trimmed, 0)];
    };

    const normalizeExampleDocs = (value) => normalizeExampleEntries(value).map(item => item.texto).filter(Boolean);

    const getExampleCount = (value) => normalizeExampleEntries(value).length;

    const getExampleUrl = (example) => {
        if (!example?.ruta_archivo) return '';
        return `${API_BASE_URL}${example.ruta_archivo}`;
    };

    const isImageExample = (example) => typeof example?.mimetype === 'string' && example.mimetype.startsWith('image/');

    const isPdfExample = (example) => typeof example?.mimetype === 'string' && example.mimetype.includes('pdf');

    const loadExamples = async (typeId, force = false) => {
        if (!typeId) return [];
        if (!force && examplesByType[typeId]) {
            return examplesByType[typeId];
        }

        setExamplesLoadingId(typeId);
        setExamplesError('');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos/${typeId}/documento-ejemplo`);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'No se pudieron cargar los ejemplos');
            }

            const data = await response.json();
            const ejemplos = normalizeExampleEntries(data.ejemplos);
            setExamplesByType(prev => ({ ...prev, [typeId]: ejemplos }));
            return ejemplos;
        } catch (err) {
            setExamplesError(err.message || 'Error al cargar los ejemplos');
            return [];
        } finally {
            setExamplesLoadingId(null);
        }
    };

    const openExamplesModal = async (tipo) => {
        setSelectedExamplesType(tipo);
        await loadExamples(tipo.id, true);
    };

    const closeExamplesModal = () => {
        setSelectedExamplesType(null);
        setExamplesError('');
    };

    const handleDeleteExample = async (typeId, exampleId) => {
        if (!window.confirm('¿Eliminar este archivo de ejemplo?')) return;

        setDeletingExampleId(exampleId);
        setExamplesError('');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos/${typeId}/documento-ejemplo/${exampleId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'No se pudo eliminar el ejemplo');
            }

            const data = await response.json();
            const ejemplos = normalizeExampleEntries(data.ejemplos);
            setExamplesByType(prev => ({ ...prev, [typeId]: ejemplos }));
            await fetchTipos();
        } catch (err) {
            setExamplesError(err.message || 'Error al eliminar el ejemplo');
        } finally {
            setDeletingExampleId('');
        }
    };

    const fetchTipos = async () => {
        try {
            console.log('Usuario:', usuario);
            console.log('Empresa ID:', usuario.empresa_id);
            
            if (!usuario.empresa_id) {
                setError('No se encontró ID de empresa. Usuario no autenticado correctamente.');
                setLoading(false);
                return;
            }
            
            const url = `${API_BASE_URL}/api/tipo-documentos?empresa_id=${usuario.empresa_id}`;
            console.log('Fetching from:', url);
            
            const response = await fetchWithAuth(url);
            console.log('Response status:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Tipos recibidos:', data);
                setTipos(data);
            } else {
                const errorData = await response.json();
                console.error('Error response:', errorData);
                setError(`Error al cargar: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Error en fetchTipos:', err);
            setError('Error al cargar tipos de documentos: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchVencimientos = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/tipos-vencimiento`);
            if (response.ok) setVencimientos(await response.json());
        } catch (err) { }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = editingId
                ? `${API_BASE_URL}/api/tipo-documentos/${editingId}`
                : `${API_BASE_URL}/api/tipo-documentos`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, empresa_id: usuario.empresa_id })
            });

            if (response.ok) {
                fetchTipos();
                setShowForm(false);
                setEditingId(null);
                setFormData({
                    tipo_documento: '',
                    descripcion: '',
                    prompt: '',
                    tipo_vencimiento: 'fijo',
                    dia_vencimiento: '',
                    tipo_entidad: 'P',
                    activo: true,
                    base: false
                });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (id, files) => {
        const archivos = Array.from(files || []);
        if (!archivos.length) return;

        const uploadData = new FormData();
        archivos.forEach(file => uploadData.append('archivo', file));

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos/${id}/documento-ejemplo`, {
                method: 'POST',
                body: uploadData
            });
            if (response.ok) {
                alert(archivos.length > 1 ? 'Documentos ejemplo cargados y texto extraído' : 'Documento ejemplo cargado y texto extraído');
                if (selectedExamplesType?.id === id) {
                    await loadExamples(id, true);
                }
                fetchTipos();
            }
        } catch (err) {
            alert('Error al cargar archivo');
        }
    };

    const handleEdit = (t) => {
        setEditingId(t.id);
        setFormData({
            tipo_documento: t.tipo_documento,
            descripcion: t.descripcion || '',
            prompt: t.prompt || '',
            tipo_vencimiento: t.tipo_vencimiento || 'fijo',
            dia_vencimiento: t.dia_vencimiento || '',
            tipo_entidad: t.tipo_entidad || 'P',
            activo: t.activo,
            base: t.base || false
        });
        setShowForm(true);
    };

    const currentExamples = selectedExamplesType
        ? (examplesByType[selectedExamplesType.id] || [])
        : [];

    const tiposFiltrados = tipos.filter(t => {
        const matchTexto = !filtroTexto ||
            (t.tipo_documento && t.tipo_documento.toLowerCase().includes(filtroTexto.toLowerCase())) ||
            (t.descripcion && t.descripcion.toLowerCase().includes(filtroTexto.toLowerCase()));
        const matchEntidad = !filtroEntidad || t.tipo_entidad === filtroEntidad;
        const matchActivo = !filtroActivo ||
            (filtroActivo === 'true' && t.activo) ||
            (filtroActivo === 'false' && !t.activo);
        return matchTexto && matchEntidad && matchActivo;
    });

    return (
        <div className="manager-container">
            <div className="header-actions">
                <h2>Documentos</h2>
                <button onClick={() => { setShowForm(true); setEditingId(null); }} className="add-button">
                    + Nuevo Tipo de Documento
                </button>
            </div>

            {showForm && (
                <div className="modal">
                    <div style={{ width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', backgroundColor: 'white', borderRadius: 'var(--radius-md)' }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)', backgroundColor: 'var(--bg-card)', borderRadius: 'var(--radius-md) var(--radius-md) 0 0' }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '8px', color: 'var(--text-primary)' }}>{editingId ? '📝 Editar Configuración' : '🆕 Nueva Configuración de Documento'}</h3>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '0' }}>Define los parámetros de validación para la Inteligencia Artificial.</p>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="standard-form" style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column' }}>
                            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="form-group">
                                    <label>Nombre del Documento</label>
                                    <input
                                        value={formData.tipo_documento}
                                        onChange={e => setFormData({ ...formData, tipo_documento: e.target.value })}
                                        placeholder="Ej: Liquidación de Sueldo"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Entidad (Aplica a)</label>
                                    <select
                                        value={formData.tipo_entidad}
                                        onChange={e => setFormData({ ...formData, tipo_entidad: e.target.value })}
                                    >
                                        <option value="P">Personas</option>
                                        <option value="E">Empresas</option>
                                        <option value="V">Vehículos</option>
                                        <option value="M">Máquinas</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label>Descripción</label>
                                <textarea
                                    rows="2"
                                    value={formData.descripcion}
                                    onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                                    placeholder="Describe el propósito y características de este tipo de documento"
                                    style={{
                                        resize: 'vertical',
                                        minHeight: '60px',
                                        fontSize: '0.95rem',
                                        lineHeight: '1.5',
                                        padding: '12px'
                                    }}
                                />
                            </div>

                            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                <div className="form-group">
                                    <label>Tipo de Vencimiento</label>
                                    <select
                                        value={formData.tipo_vencimiento}
                                        onChange={e => setFormData({ ...formData, tipo_vencimiento: e.target.value, dia_vencimiento: e.target.value === 'MF' ? formData.dia_vencimiento : '' })}
                                    >
                                        {vencimientos.map(v => (
                                            <option key={v.codigo} value={v.codigo}>{v.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Estado del Tipo de Documento</label>
                                    <select
                                        value={formData.activo}
                                        onChange={e => setFormData({ ...formData, activo: e.target.value === 'true' })}
                                    >
                                        <option value="true">Activo</option>
                                        <option value="false">Inactivo</option>
                                    </select>
                                </div>
                            </div>

                            {formData.tipo_vencimiento === 'MF' && (
                                <div className="form-group" style={{ marginTop: '16px' }}>
                                    <label>Día de Vencimiento (1-31)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={formData.dia_vencimiento}
                                        onChange={e => setFormData({ ...formData, dia_vencimiento: e.target.value })}
                                        placeholder="Ej: 15 (día del mes de vencimiento)"
                                    />
                                </div>
                            )}

                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.base}
                                        onChange={e => setFormData({ ...formData, base: e.target.checked })}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span style={{ fontSize: '1rem', fontWeight: '500' }}>Es Documento Base</span>
                                </label>
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginLeft: '26px', marginTop: '4px' }}>
                                    Marcar si este documento es un requisito base para la entidad seleccionada.
                                </p>
                            </div>

                            <div className="form-group" style={{ marginTop: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <label style={{ fontSize: '1rem', fontWeight: '600' }}>🔍 Instrucciones para Análisis IA</label>
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '8px', marginTop: '4px' }}>Describe con detalle qué información debe buscar la IA en el documento.</p>
                                <textarea
                                    value={formData.prompt}
                                    onChange={e => setFormData({ ...formData, prompt: e.target.value })}
                                    placeholder="Indica qué campos extraer. Ej: 'Extrae el nombre del trabajador, rut, periodo pagado y monto bruto'"
                                    style={{
                                        resize: 'none',
                                        flex: 1,
                                        minHeight: '150px',
                                        fontSize: '0.95rem',
                                        lineHeight: '1.5',
                                        padding: '12px'
                                    }}
                                />
                            </div>

                            <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                                <button type="submit" disabled={loading} className="add-button">
                                    {loading ? 'Guardando...' : 'Guardar Configuración'}
                                </button>
                                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {selectedExamplesType && (
                <div className="modal">
                    <div style={{ width: '100%', maxWidth: '980px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', backgroundColor: 'white', borderRadius: 'var(--radius-md)' }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)', backgroundColor: 'var(--bg-card)', borderRadius: 'var(--radius-md) var(--radius-md) 0 0', display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
                            <div>
                                <h3 style={{ fontSize: '1.5rem', marginBottom: '8px', color: 'var(--text-primary)' }}>📎 Ejemplos de {selectedExamplesType.tipo_documento}</h3>
                                <p style={{ color: 'var(--text-muted)', marginBottom: '0' }}>Abre, revisa o elimina los archivos de ejemplo usados por la IA.</p>
                            </div>
                            <button type="button" onClick={closeExamplesModal} className="btn-secondary">
                                Cerrar
                            </button>
                        </div>

                        <div style={{ padding: '24px', overflowY: 'auto' }}>
                            {examplesLoadingId === selectedExamplesType.id && <p>Cargando ejemplos...</p>}
                            {examplesError && (
                                <p style={{ color: 'var(--danger, #dc2626)', marginBottom: '16px' }}>{examplesError}</p>
                            )}

                            {!examplesLoadingId && currentExamples.length === 0 && (
                                <p style={{ color: 'var(--text-muted)' }}>No hay archivos de ejemplo cargados para este tipo de documento.</p>
                            )}

                            <div style={{ display: 'grid', gap: '12px' }}>
                                {currentExamples.map((example) => (
                                    <div
                                        key={example.example_id}
                                        style={{
                                            border: '1px solid var(--border-light)',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '16px',
                                            alignItems: 'flex-start',
                                            backgroundColor: '#fff'
                                        }}
                                    >
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <strong style={{ color: 'var(--text-primary)' }}>
                                                    {example.nombre_archivo || 'Archivo sin nombre'}
                                                </strong>
                                                {example.legacy && (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Legacy</span>
                                                )}
                                                {isImageExample(example) && (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>Imagen</span>
                                                )}
                                                {isPdfExample(example) && (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>PDF</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                {example.mimetype ? `Tipo: ${example.mimetype}` : 'Tipo no disponible'}
                                                {example.fecha_creacion ? ` · ${new Date(example.fecha_creacion).toLocaleString('es-CL')}` : ''}
                                            </div>
                                            {example.ruta_archivo && isImageExample(example) && (
                                                <div style={{ marginTop: '12px' }}>
                                                    <img
                                                        src={getExampleUrl(example)}
                                                        alt={example.nombre_archivo || 'Ejemplo de imagen'}
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '420px',
                                                            maxHeight: '260px',
                                                            objectFit: 'contain',
                                                            borderRadius: '10px',
                                                            border: '1px solid var(--border-light)',
                                                            background: '#fff'
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {example.ruta_archivo && isPdfExample(example) && (
                                                <div style={{ marginTop: '12px' }}>
                                                    <iframe
                                                        src={getExampleUrl(example)}
                                                        title={example.nombre_archivo || 'Ejemplo PDF'}
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '420px',
                                                            height: '260px',
                                                            border: '1px solid var(--border-light)',
                                                            borderRadius: '10px',
                                                            background: '#fff'
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            <p style={{ marginTop: '10px', marginBottom: '0', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {example.texto || 'No hay texto extraído para este archivo.'}
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                disabled={!example.ruta_archivo}
                                                onClick={() => window.open(getExampleUrl(example), '_blank', 'noopener,noreferrer')}
                                            >
                                                Ver
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                disabled={deletingExampleId === example.example_id}
                                                onClick={() => handleDeleteExample(selectedExamplesType.id, example.example_id)}
                                            >
                                                {deletingExampleId === example.example_id ? 'Eliminando...' : 'Eliminar'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {loading && !tipos.length ? <p>Cargando configuraciones...</p> : (
                <>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1, minWidth: '200px', margin: 0 }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-muted)' }}>Buscar</label>
                        <input
                            type="text"
                            value={filtroTexto}
                            onChange={e => setFiltroTexto(e.target.value)}
                            placeholder="Nombre o descripción..."
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="form-group" style={{ minWidth: '160px', margin: 0 }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-muted)' }}>Entidad</label>
                        <select
                            value={filtroEntidad}
                            onChange={e => setFiltroEntidad(e.target.value)}
                        >
                            <option value="">Todas</option>
                            <option value="P">👤 Personas</option>
                            <option value="E">🏢 Empresas</option>
                            <option value="V">🚗 Vehículos</option>
                            <option value="M">🚜 Máquinas</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ minWidth: '140px', margin: 0 }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-muted)' }}>Estado</label>
                        <select
                            value={filtroActivo}
                            onChange={e => setFiltroActivo(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                        </select>
                    </div>
                    {(filtroTexto || filtroEntidad || filtroActivo) && (
                        <button
                            type="button"
                            onClick={() => { setFiltroTexto(''); setFiltroEntidad(''); setFiltroActivo(''); }}
                            className="btn-secondary"
                            style={{ height: '40px' }}
                        >
                            ✕ Limpiar
                        </button>
                    )}
                </div>
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>Documento</th>
                            <th>Descripción</th>
                            <th>Vencimiento</th>
                            <th>Aplica a</th>
                            <th>Base</th>
                            <th>IA Prompt</th>
                            <th>Documento Base</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tiposFiltrados.map(t => (
                            <tr key={t.id}>
                                <td data-label="Documento" style={{ fontWeight: '600' }}>{t.tipo_documento}</td>
                                <td data-label="Descripción" title={t.descripcion} style={{ maxWidth: '250px' }}>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--text-muted)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {t.descripcion || '---'}
                                    </div>
                                </td>
                                <td data-label="Vencimiento">
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                        📅 {t.tipo_vencimiento}{t.tipo_vencimiento === 'MF' && t.dia_vencimiento ? ` (día ${t.dia_vencimiento})` : ''}
                                    </span>
                                </td>
                                <td data-label="Aplica a">
                                    <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        background: '#f1f5f9',
                                        color: 'var(--sidebar-bg)',
                                        fontWeight: '600'
                                    }}>
                                        {t.tipo_entidad === 'P' && '👤 Personas'}
                                        {t.tipo_entidad === 'E' && '🏢 Empresas'}
                                        {t.tipo_entidad === 'V' && '🚗 Vehículos'}
                                        {t.tipo_entidad === 'M' && '🚜 Máquinas'}
                                        {!t.tipo_entidad && '---'}
                                    </span>
                                </td>
                                <td data-label="Base">
                                    <span style={{
                                        fontSize: '1.2rem',
                                        textAlign: 'center',
                                        display: 'block'
                                    }}>
                                        {t.base ? '✅' : '❌'}
                                    </span>
                                </td>
                                <td data-label="IA Prompt" title={t.prompt} style={{ maxWidth: '250px' }}>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--text-muted)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {t.prompt || '⚠️ Sin instrucciones de IA'}
                                    </div>
                                </td>
                                <td data-label="Documento Base">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            multiple
                                            id={`file-${t.id}`}
                                            onChange={(e) => handleFileUpload(t.id, e.target.files)}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            onClick={() => document.getElementById(`file-${t.id}`).click()}
                                            style={{
                                                fontSize: '0.75rem',
                                                padding: '4px 8px',
                                                background: '#f1f5f9',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {getExampleCount(t.documento_ejemplo) > 0 ? '🔄 Agregar ejemplos' : '➕ Subir Base'}
                                        </button>
                                        {getExampleCount(t.documento_ejemplo) > 0 && (
                                            <span style={{ color: 'var(--success)', fontSize: '0.75rem' }}>
                                                ✅ {getExampleCount(t.documento_ejemplo)} ejemplo(s)
                                            </span>
                                        )}
                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', width: '100%' }}>
                                            PDF, PNG, JPG o JPEG como referencia.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => openExamplesModal(t)}
                                            disabled={getExampleCount(t.documento_ejemplo) === 0}
                                            style={{
                                                fontSize: '0.75rem',
                                                padding: '4px 8px',
                                                background: '#fff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '4px',
                                                cursor: getExampleCount(t.documento_ejemplo) === 0 ? 'not-allowed' : 'pointer',
                                                opacity: getExampleCount(t.documento_ejemplo) === 0 ? 0.6 : 1
                                            }}
                                        >
                                            👁️ Ver ejemplos
                                        </button>
                                    </div>
                                </td>
                                <td data-label="Acciones">
                                    <button onClick={() => handleEdit(t)} className="btn-action btn-edit">
                                        ⚙️ Configurar
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {tiposFiltrados.length === 0 && tipos.length > 0 && (
                            <tr><td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No hay documentos que coincidan con los filtros.</td></tr>
                        )}
                    </tbody>
                </table>
                </>
            )}
        </div>
    );
}
